<template>
  <layout-popup>
    <template slot="header">
      {{ isEdit ? ` Edit property ` : `Add property` }}
    </template>
    <template>
      <div class="mx-4">
        <subheader>General info</subheader>

        <v-form ref="form" v-model="isValid" lazy-validation>
          <v-combobox
            hide-details
            hide-no-data
            :items="autocompleteItems"
            clearable
            :rules="groupNameRules"
            v-model="groupName"
            label="Group *"
            required
            class="mb-4"
            placeholder="Group *"
          ></v-combobox>

          <v-text-field
            :rules="propertyRules"
            label="Property name *"
            placeholder="Property name *"
            v-model="property"
            required
            clearable
          />

          <v-text-field
            label="Long name"
            placeholder="Long name"
            v-model="description"
            required
            clearable
          />

          <v-select
            :menu-props="{ bottom: true, offsetY: true }"

            :rules="typeRules"
            :items="types"
            v-model="typeId"
            label="Type *"
            item-value="id"
            item-text="name"
            placeholder="Value"
            required
            clearable
          />

          <v-text-field
            label="Key"
            disabled
            placeholder="Key"
            v-model="prefilledGroup.key"
          />

          <div class="d-flex d-flex align-center mx-0">
            <div style="font-size: 16px">Hidden</div>
            <v-spacer />
            <v-switch
              data-test-switch="hidden"
              v-model="hidden"
              class="ma-0 py-3"
              hide-details
            ></v-switch>
          </div>
          <div class="d-flex d-flex align-center mx-0">
            <div style="font-size: 16px">Stealth</div>
            <v-spacer />
            <v-switch
              data-test-switch="stealth"
              v-model="stealth"
              class="ma-0 py-3"
              hide-details
            ></v-switch>
          </div>

          <subheader>Optional fields</subheader>

          <v-text-field
            label="Units"
            v-model="units"
            placeholder="Value"
            clearable
          />
          <span class="subtitle-1" v-if="isJson(typeId)">Default value </span>
          <code-editor
            v-model="defaultValue"
            v-if="isJson(typeId)"
            :code="defaultValue"
          ></code-editor>
          <v-select
            :menu-props="{ bottom: true, offsetY: true }"

            v-else-if="isBoolean(typeId)"
            label="Default value"
            v-model="defaultValue"
            item-value="id"
            item-text="name"
            :items="[
              { id: true, name: 'true' },
              { id: false, name: 'false' },
              { id: null, name: 'null' }
            ]"
          ></v-select>
          <v-text-field
            v-else
            label="Default value"
            :type="isNumber(typeId) ? 'number' : ''"
            placeholder="value"
            v-model="defaultValue"
            clearable
          />
          <div class="mt-2" v-if="isNonBasicType(typeId)">
            <span class="subtitle-1">JSON schema </span>
            <code-editor v-model="jsonSchema" :code="jsonSchema"></code-editor>
          </div>

          <v-text-field
            label="Regex"
            v-model="regex"
            placeholder="Value"
            clearable
          />

          <v-text-field
            v-if="isNumber(typeId)"
            label="Value range"
            placeholder='{ "min": 1, "max": 100 }'
            v-model="valueRange"
            clearable
          />

          <span class="subtitle-1"
            >Value set
            <span class="caption">{"Option1": "value1" ...}</span>
          </span>
          <code-editor
            v-model="valueSet"
            :code="
              prefilledGroup.valueSet
                ? JSON.stringify(prefilledGroup.valueSet)
                : ''
            "
          ></code-editor>

          <v-text-field
            label="Mask"
            v-model="mask"
            placeholder="value"
            clearable
          />
        </v-form>
      </div>
    </template>
    <template slot="footer">
      <v-spacer />
      <v-btn text @click="$emit('cancel')">
        cancel
      </v-btn>
      <v-btn color="primary" :loading="loading" @click="beforeSend" text>
        {{ isEdit ? 'save' : 'add' }}
      </v-btn>
    </template>
  </layout-popup>
</template>

<script>
import LayoutPopup from '@/layouts/LayoutPopup';
import isEqual from 'lodash.isequal';
import {
  toRefs,
  reactive,
  computed,
  onMounted,
  watch,
  ref
} from '@vue/composition-api';
import CodeEditor from '@/components/CodeEditor';
import {
  isBoolean,
  isJson,
  isNonBasicType,
  isNumber
} from '@/utils/getCurrentType';
import isJsonString from '@/utils/validation/isJson';
import { cloneDeep } from '@apollo/client/utilities';

export default {
  name: 'ActionSchemaProperty',
  components: { CodeEditor, LayoutPopup },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    typeView: {
      type: String,
      default: ''
    },
    prefilledGroup: {
      type: [String, Object],
      default: ''
    },
    editedControl: {
      type: Object,
      default: () => ({})
    },
    autocompleteItems: {
      type: Array,
      default: () => []
    },
    types: {
      type: Array,
      default: () => []
    },
    schemaId: {
      type: String,
      required: true
    }
  },
  setup(props, { emit, refs, root }) {
    const cloned = ref(null);
    const state = reactive({
      typeOptions: ['double', 'int', 'string', 'bool', 'json'],
      isValid: true,
      defaultValue: null,
      jsonSchema: null,
      description: '',
      groupName: '',
      hidden: false,
      stealth: true,
      isJson: false,
      mask: '',
      property: '',
      regex: '',
      typeId: '',
      units: '',
      longName: '',
      valueRange: null,
      valueSet: null,
      groupNameRules: [v => !!v || 'Group name is required'],
      propertyRules: [v => !!v || 'Property is required'],
      typeRules: [v => !!v || 'type is required'],
      jsonRules: [v => isJsonString(v) || 'invalid json']
    });
    watch(
      () => props.editedControl,
      value => {
        if (typeof props.editedControl !== 'string') {
          for (let prop in value) {
            // eslint-disable-next-line no-prototype-builtins
            if (value.hasOwnProperty(prop)) {
              state[prop] = value[prop];
            }
          }
        }
      }
    );

    const isEdit = computed(() => {
      return props.typeView.includes('edit');
    });

    const beforeSend = async () => {
      refs.form.validate();
      await root.$nextTick();
      if (state.isValid) {
        setTimeout(sendForm, 0);
      }
    };

    const prepareBeforeSend = value => {
      if (isJson(state.typeId)) {
        if (typeof value === 'string') {
          return JSON.parse(value);
        } else {
          return value;
        }
      } else if (isNumber(state.typeId)) {
        return +value;
      }
      return value;
    };

    const sendForm = () => {
      let keys = [
        'defaultValue',
        'description',
        'groupName',
        'jsonSchema',
        'hidden',
        'stealth',
        'mask',
        'property',
        'regex',
        'typeId',
        'units',
        'valueRange',
        'valueSet',
        'index'
      ];
      const createData = () => ({
        defaultValue: prepareBeforeSend(state.defaultValue),
        jsonSchema: JSON.parse(state.jsonSchema),
        description: state.description || null,
        groupName: state.groupName,
        hidden: state.hidden,
        stealth: state.stealth,
        mask: state.mask,
        property: state.property,
        regex: state.regex,
        typeId: state.typeId,
        units: state.units,
        valueRange: state.valueRange ? JSON.parse(state.valueRange) : null,
        valueSet: state.valueSet ? JSON.parse(state.valueSet) : null,
        index: '0',
        schemaId: props.schemaId
      });

      let beforeSend = createData();

      if (cloned.value) {
        keys.forEach(key => {
          if (isEqual(cloned.value[key], beforeSend[key])) {
            delete beforeSend[key];
          }
        });
      }

      if (Object.keys(beforeSend).length) {
        emit('action-control', {
          type: props.typeView,
          id: props.editedControl?.id,
          formData: beforeSend
        });
      } else {
        emit('cancel');
      }
    };

    onMounted(() => {
      if (props.prefilledGroup) {
        if (typeof props.prefilledGroup === 'string') {
          state.groupName = props.prefilledGroup;
        } else {
          state.groupName = props.prefilledGroup.groupName;
          state.defaultValue = isJson(props.prefilledGroup.typeId)
            ? JSON.stringify(props.prefilledGroup.defaultValue)
            : props.prefilledGroup.defaultValue;
          state.description = props.prefilledGroup.description;
          state.jsonSchema = JSON.stringify(props.prefilledGroup.jsonSchema);
          state.hidden = props.prefilledGroup.hidden;
          state.stealth = props.prefilledGroup.stealth;
          state.mask = props.prefilledGroup.mask;
          state.property = props.prefilledGroup.property;
          state.regex = props.prefilledGroup.regex;
          state.typeId = props.prefilledGroup.typeId;
          state.units = props.prefilledGroup.units;
          state.valueRange = props.prefilledGroup.valueRange
            ? JSON.stringify(props.prefilledGroup.valueRange)
            : '';
          state.valueSet = props.prefilledGroup.valueSet
            ? JSON.stringify(props.prefilledGroup.valueSet)
            : '';
          state.index = props.prefilledGroup.index;

          cloned.value = cloneDeep(props.prefilledGroup);
        }
      }
    });
    return {
      ...toRefs(state),
      isEdit,
      sendForm,
      beforeSend,
      isJson,
      isNumber,
      isBoolean,
      isJsonString,
      isNonBasicType
    };
  }
};
</script>
